import {
  IncrementalRoasTakeaways,
  ModelOverview
} from '@blisspointmedia/bpm-types/dist/BrandEquity';
import { currencyFormatter } from '@blisspointmedia/bpm-types/dist/MMMUtils';
import { formatNumber, ZERO_LABEL } from '@blisspointmedia/bpm-types/dist/formatUtils';
import { DynamicModelOverviewSection } from '@Components/DynamicModelOverview/DynamicModelOverview';
import { dateToMddyy } from '@src/utils/date-utils';
import { percFormat } from './BrandImpact/BrandImpactUtils';

const MAPE_TOOLTIP =
  "Mean absolute percentage error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in percentage terms. Its definition is avg(abs((predicted - actual)/actual)).";
const MAE_TOOLTIP =
  "Mean absolute error is a measure of the model's prediction accuracy, reflecting the average magnitude of the difference between actual and predicted values in absolute terms. Its definition is avg(abs(predicted - actual)).";

// The four-section DynamicModelOverview summary shared by both Model Overview panels (Brand
// Impact + Business Impact). They diverge only in the outcome-variable source (group-by metric
// vs modelOverview.kpi) and the recency date (Brand falls back to refresh_timestamp) — threaded
// in via `outcomeVariable` and `refreshDate`.
export const buildModelOverviewSections = (
  modelOverview: ModelOverview,
  incrementalRoasTakeaways: IncrementalRoasTakeaways,
  outcomeVariable: string,
  refreshDate: string
): DynamicModelOverviewSection[] => [
  {
    title: 'Training Data',
    rows: [
      { label: 'First model observation', value: dateToMddyy(modelOverview.train_start_date) },
      { label: 'Last model observation', value: dateToMddyy(modelOverview.test_end_date) },
      {
        label: 'Average weekly spend',
        value: currencyFormatter.format(modelOverview.mean_spend, 0)
      },
      { label: 'Outcome variable', value: outcomeVariable }
    ]
  },
  {
    title: 'Recency',
    rows: [{ label: 'Last refresh date', value: dateToMddyy(refreshDate) }]
  },
  {
    title: 'Statistics',
    rows: [
      {
        label: 'Out-of-sample MAPE',
        value: percFormat(modelOverview.test_mape),
        tooltipText: MAPE_TOOLTIP
      },
      {
        label: 'Out-of-sample MAE',
        value: formatNumber(modelOverview.test_mae, 2),
        tooltipText: MAE_TOOLTIP
      }
    ]
  },
  {
    title: 'Key Results',
    rows: [
      { label: 'Incremental revenue from media-driven brand equity', value: null },
      {
        label: 'TTM Incremental Revenue',
        value: `$${(incrementalRoasTakeaways.spendInducedBrandRevenue / 1000000).toFixed(2)}M`
      },
      {
        label: 'TTM Media Spend',
        value: `$${(incrementalRoasTakeaways.paidMediaSpend / 1000000).toFixed(2)}M`
      },
      {
        label: 'TTM Incremental ROAS',
        value: Number.isFinite(incrementalRoasTakeaways.iRoasTtm)
          ? incrementalRoasTakeaways.iRoasTtm.toFixed(2)
          : ZERO_LABEL
      }
    ]
  }
];
